import {
  Contract,
  ContractArchiveDocument,
  ContractAttachment,
  ContractLightSearch,
  ContractOrdersResponse,
  ContractOrderType,
  ContractRemainingDto,
  ContractStatus,
  CreateApproval,
  CreateContract,
  DocumentBase64,
  DocumentResponse,
  FileType,
  PriceIncreaseType,
} from "@/types";
import { keepPreviousData, QueryClient, useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { APIBASE_REVIVERCONTRACTS, axios } from "./ApiConfig";
import ContractSortTypeEnum from "@/constants/enums/ContractSortTypeEnum";
import { arrayBufferToBase64 } from "@/utils/arrayBufferToBase64";

const getContractsFull = async (sortOption?: any, order?: any, showDeleted = false) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/search/all-full`, {
    params: {
      sortOption,
      order,
      showDeleted,
    },
  });

export const useGetContractsFull = (sortOption?: any, order?: any, showDeleted = false, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Contract[] | undefined>({
    queryKey: ["getContractsFull", sortOption, order, showDeleted],
    queryFn: async () => {
      const result = await getContractsFull(sortOption, order, showDeleted);
      return result.data;
    },
    ...queryOptions,
  });
};

interface ContractLightParams {
  sortOption?: ContractSortTypeEnum;
  limit: number;
  showDeleted?: boolean;
  sortDirection?: "ASC" | "DESC";
  sortBy?: string;
  statuses?: ContractStatus[];
  pageNumber: number;
  query?: string;
}

const getContractsLight = async (params: ContractLightParams) => {
  return axios.get(`${APIBASE_REVIVERCONTRACTS}api/search/all`, {
    params: {
      limit: params.limit,
      pageNumber: params.pageNumber,
      sortOption: params.sortOption === ContractSortTypeEnum.BY_ID ? null : params.sortOption,
      sortDirection: params.sortDirection,
      showDeleted: params.showDeleted,
      statuses: params.statuses,
      contractName: params.query,
    },
  });
};

export const useGetContractsLight = (
  params: ContractLightParams,
  options?: Partial<UseQueryOptions<ContractLightSearch>>
) => {
  const queryOptions = {
    ...options,
  };

  return useQuery<ContractLightSearch>({
    queryKey: [
      "getContractsLight",
      params.limit,
      params.pageNumber,
      params.sortOption,
      params.sortDirection,
      params.showDeleted,
      params.statuses,
      params.query,
    ],
    queryFn: async () => {
      const result = await getContractsLight(params);
      return result.data;
    },
    ...queryOptions,
  });
};

const getContractById = async (contractId: number) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}`);

export const useGetContractById = (contractId: number, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Contract | undefined>({
    queryKey: ["getContractById", contractId],
    queryFn: async () => {
      const result = await getContractById(contractId);
      return result.data;
    },
    ...queryOptions,
  });
};

interface ContractOrdersParams {
  page: number;
  pageSize: number;
  /** Omit to retrieve both BUY and SELL orders */
  orderType?: ContractOrderType;
}

const getContractOrders = async (contractId: number, params: ContractOrdersParams) =>
  axios.get<ContractOrdersResponse>(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/orders`, {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      orderType: params.orderType,
    },
  });

export const useGetContractOrders = (
  contractId: number,
  params: ContractOrdersParams,
  options?: Partial<UseQueryOptions<ContractOrdersResponse>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    ...options,
  };

  return useQuery<ContractOrdersResponse>({
    queryKey: ["getContractOrders", contractId, params.orderType, params.page, params.pageSize],
    queryFn: async () => {
      const result = await getContractOrders(contractId, params);
      return result.data;
    },
    ...queryOptions,
  });
};

export const prefetchContractById = (queryClient: QueryClient, contractId: number) =>
  queryClient.prefetchQuery({
    queryKey: ["getContractById", contractId],
    queryFn: async () => (await getContractById(contractId)).data as Contract,
    staleTime: 30_000,
  });

export const prefetchContractsLight = (queryClient: QueryClient, params: ContractLightParams) =>
  queryClient.prefetchQuery({
    queryKey: [
      "getContractsLight",
      params.limit,
      params.pageNumber,
      params.sortOption,
      params.sortDirection,
      params.showDeleted,
      params.statuses,
      params.query,
    ],
    queryFn: async () => (await getContractsLight(params)).data as ContractLightSearch,
    staleTime: 30_000,
  });

export const postCreateContract = async (contract: CreateContract, taskId?: number | null) => {
  const config = taskId ? { headers: { TaskId: taskId } } : {};
  return axios.post(`${APIBASE_REVIVERCONTRACTS}api/contracts/create`, contract, config);
};

export const deleteContract = async (contractId: number) =>
  axios.delete(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/delete`);

export const updateContract = async (contract: CreateContract, contractId: number, companyId: number) =>
  axios.put(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/update`, contract, {
    params: {
      companyId,
    },
  });

export const postApproveContract = async (contractId: number, approvalBody: CreateApproval) =>
  axios.post(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/approvals`, approvalBody);

export const postArchiveContract = async (contractId: number, taskId: number, answer: "ACCEPT" | "DECLINE") => {
  const taskConfig = {
    headers: {
      TaskId: taskId,
    },
  };
  return axios.post(
    `${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/archive/consent`,
    {
      type: answer,
    },
    taskConfig
  );
};

export const postProcessOrder = async (orderId: number) =>
  axios.post(`${APIBASE_REVIVERCONTRACTS}api/orders/${orderId}/approve`);

const getIsProductUsedOnContract = async (companySpecificProductId: number, orderType: string) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/isProductUsedInContract/${companySpecificProductId}`, {
    params: { orderType },
  });

export const useGetIsProductUsedOnContract = (companySpecificProductId: number, orderType: string, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Contract[] | undefined>({
    queryKey: ["getIsProductUsedOnContract", companySpecificProductId, orderType],
    queryFn: async () => {
      const result = await getIsProductUsedOnContract(companySpecificProductId, orderType);
      return result.data;
    },
    ...queryOptions,
  });
};

const getIsSalesItemAndConditionUsedInContract = async (salesItemId: number, conditionId: number, orderType: string) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/isSalesItemAndConditionUsedInContract`, {
    params: { salesItemId, conditionId, orderType },
  });

export const useGetIsSalesItemAndConditionUsedInContract = (
  salesItemId: number,
  conditionId: number,
  orderType: string,
  options?: any
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Contract[] | undefined>({
    queryKey: ["getIsSalesItemAndConditionUsedInContract", salesItemId, conditionId, orderType],
    queryFn: async () => {
      const result = await getIsSalesItemAndConditionUsedInContract(salesItemId, conditionId, orderType);
      return result.data;
    },
    ...queryOptions,
  });
};

const activeContractsCount = async (companyId: number, from: string, to: string) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/activeContractsCount`, {
    params: { companyId, from, to },
  });

export const useGetActiveContractsCount = (companyId: number, from: string, to: string, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<number | undefined>({
    queryKey: ["getActiveContractsCount", companyId, from, to],
    queryFn: async () => {
      const result = await activeContractsCount(companyId, from, to);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getContractAttachments = async (contractId: number) => {
  return axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/attachments`);
};

export const useGetContractAttachments = (contractId: number, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    enabled: contractId !== undefined,
    ...options,
  };

  return useQuery<ContractAttachment[]>({
    queryKey: ["getContractAttachments", contractId],
    queryFn: async () => {
      const result = await getContractAttachments(contractId);
      console.log(result);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getContractProductImages = async (productId: number) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/product/${productId}/images`);

export const getContractProductAttachments = async (productId: number) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/product/${productId}/attachments`);

export const getContractProductAttachment = async (productId: number, attachmentId: string) => {
  return axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/product/${productId}/attachments/${attachmentId}`, {
    responseType: "arraybuffer", // Change to arraybuffer for base64 conversion
    timeout: 30000,
  });
};

export const getContractProductAttachmentsAsBase64 = async (productId: number, attachmentIds: string[]) => {
  const attachmentPromises = attachmentIds.map(async (attachmentId) => {
    try {
      const response = await getContractProductAttachment(productId, attachmentId);
      const base64String = arrayBufferToBase64(response.data);
      let contentType = response.headers["content-type"] || "application/octet-stream";

      // Fix content type for PDFs that are returned as octet-stream
      if (contentType === "application/octet-stream") {
        const uint8Array = new Uint8Array(response.data);
        const pdfSignature = [0x25, 0x50, 0x44, 0x46]; // "%PDF" in bytes

        if (
          uint8Array.length >= 4 &&
          uint8Array[0] === pdfSignature[0] &&
          uint8Array[1] === pdfSignature[1] &&
          uint8Array[2] === pdfSignature[2] &&
          uint8Array[3] === pdfSignature[3]
        ) {
          contentType = "application/pdf";
        }
      }

      if (response.data.byteLength === 0) {
        return {
          id: attachmentId,
          base64: null,
          contentType: null,
          url: null,
          error: "Empty response received",
        };
      }

      return {
        id: attachmentId,
        base64: base64String,
        contentType: contentType,
        url: `data:${contentType};base64,${base64String}`,
      };
    } catch (error) {
      return {
        id: attachmentId,
        base64: null,
        contentType: null,
        url: null,
        error: error,
      };
    }
  });

  return Promise.all(attachmentPromises);
};

export const useGetContractProductAttachments = (productId: number, attachmentIds: string[] | null, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    enabled: productId !== undefined && attachmentIds && attachmentIds.length > 0,
    ...options,
  };

  return useQuery<DocumentBase64[]>({
    queryKey: ["getContractProductAttachments", productId, attachmentIds],
    queryFn: async () => {
      return await getContractProductAttachmentsAsBase64(productId, attachmentIds as string[]);
    },
    ...queryOptions,
  });
};

export const getContractProductImage = async (productId: number, imageId: string) => {
  return axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/product/${productId}/images/${imageId}`, {
    responseType: "arraybuffer", // Change back to arraybuffer for base64 conversion
    headers: {
      Accept: "image/jpeg,image/png,image/*",
    },
    timeout: 15000,
  });
};

export const getContractProductImagesAsBase64 = async (productId: number, imageIds: string[]) => {
  const imagePromises = imageIds.map(async (imageId) => {
    try {
      const response = await getContractProductImage(productId, imageId);

      const base64String = arrayBufferToBase64(response.data);
      const contentType = response.headers["content-type"] || "image/jpeg";

      if (response.data.byteLength === 0) {
        return {
          id: imageId,
          base64: null,
          contentType: null,
          url: null,
          error: "Empty response received",
        };
      }

      return {
        id: imageId,
        base64: base64String,
        contentType: contentType,
        url: `data:${contentType};base64,${base64String}`,
      };
    } catch (error) {
      return {
        id: imageId,
        base64: null,
        contentType: null,
        url: null,
        error: error,
      };
    }
  });

  return Promise.all(imagePromises);
};

export const useGetContractProductImages = (productId: number, imageIds: string[] | null, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    enabled: productId !== undefined && imageIds && imageIds.length > 0,
    ...options,
  };

  return useQuery<DocumentBase64[]>({
    queryKey: ["getContractProductImages", productId, imageIds],
    queryFn: async () => {
      return await getContractProductImagesAsBase64(productId, imageIds as string[]);
    },
    ...queryOptions,
  });
};

export const deleteContractImage = async (contractId: number, images: string[]) =>
  axios.delete(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/images/delete`, { data: images });

export const createContractImage = async (contractId: number, images: ContractArchiveDocument[]) =>
  axios.post<DocumentResponse[]>(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/images/upload`, images);

export const deleteContractAttachment = async (contractId: number, attachments: string[]) =>
  axios.delete(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/attachments/delete`, { data: attachments });

export const createContractAttachment = async (contractId: number, attachments: File[], fileTypes: FileType[]) => {
  const uploadPromises = attachments.map((file, index) => {
    const formData = new FormData();

    formData.append("attachments", file);

    formData.append("fileTypes", fileTypes[index]);
    formData.append("documentTypes", "ATTACHMENT");

    formData.append("contractId", contractId.toString());

    return axios.post<{ documentId: string }[]>(
      `${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/attachments/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  });

  return Promise.all(uploadPromises);
};

export const getContractProductDocumentsAsByteArray = async (
  productId: number,
  documentType: "images" | "attachments",
  documentId: string
) =>
  axios
    .get(`${APIBASE_REVIVERCONTRACTS}api/contracts/product/${productId}/${documentType}/${documentId}`, {
      responseType: "arraybuffer",
    })
    .then((response) => {
      const base64ImageString = arrayBufferToBase64(response.data);
      const contentType = response.headers["content-type"];
      const url = base64ImageString;
      return { url, contentType };
    })
    .catch((error) => error);

export const useGetContractProductAttachmentsAsByteArray = (
  productId: number,
  documentType: "images" | "attachments",
  ids: string[],
  options?: any
) => {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ["getContractProductAttachmentsAsByteArray", productId, id],
      queryFn: () => getContractProductDocumentsAsByteArray(productId, documentType, id),
      staleTime: 5 * 60 * 1000,
      ...options,
    })),
    combine: (results) => {
      return {
        data: results.map((result, index) => ({
          id: ids[index],
          document: (result.data as any)?.url,
          contentType: (result.data as any)?.contentType,
          fileType: (result.data as any)?.contentType?.split("/")[1] || "unknown",
          fileName: ids[index],
          isPending: result.isLoading,
          isError: result.isError,
        })),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        errors: results.filter((result) => result.isError).map((result) => result.error),
        successCount: results.filter((result) => result.isSuccess).length,
        totalCount: results.length,
      };
    },
  });
};

export const getContractTemplates = async (companyId: number) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/templates/${companyId}`);

export const useGetContractTemplates = (companyId: number, options?: Partial<UseQueryOptions<CreateContract[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<CreateContract[]>({
    queryKey: ["getContractTemplates", companyId],
    queryFn: async () => {
      const result = await getContractTemplates(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getContractTemplate = async (contractTemplateId: number) =>
  axios.get(`${APIBASE_REVIVERCONTRACTS}api/contracts/template/${contractTemplateId}`);

export const useGetContractTemplate = (contractTemplateId: number, options?: any) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<CreateContract>({
    queryKey: ["getContractTemplate", contractTemplateId],
    queryFn: async () => {
      const result = await getContractTemplate(contractTemplateId);
      return result.data;
    },
    ...queryOptions,
  });
};
export const archiveContract = async (contractId: number, taskId?: string) => {
  const url = `${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/archive`;
  const config = taskId ? { headers: { TaskId: taskId } } : {};
  return axios.post(url, {}, config);
};

export const getArchivedContracts = async (companyId: number) =>
  axios.get<Contract[]>(`${APIBASE_REVIVERCONTRACTS}api/contracts/${companyId}/archived`);

export const useGetArchivedContracts = (companyId: number, options?: Partial<UseQueryOptions<Contract[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<Contract[]>({
    queryKey: ["getArchivedContracts", companyId],
    queryFn: async () => {
      const result = await getArchivedContracts(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getContractToRenew = async (contractId: number) =>
  axios.get<CreateContract>(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/renew`);

export const useGetContractToRenew = (contractId: number, options?: Partial<UseQueryOptions<CreateContract>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<CreateContract>({
    queryKey: ["getContractToRenew", contractId],
    queryFn: async () => {
      const result = await getContractToRenew(contractId);
      console.log(result.data);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getRemainingOnContract = async (contractId: number) =>
  axios.get<ContractRemainingDto>(`${APIBASE_REVIVERCONTRACTS}api/contracts/${contractId}/remaining`);

export const useGetRemainingOnContract = (
  contractId: number,
  options?: Partial<UseQueryOptions<ContractRemainingDto>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };
  return useQuery<ContractRemainingDto>({
    queryKey: ["getRemainingOnContract", contractId],
    queryFn: async () => {
      const result = await getRemainingOnContract(contractId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const getIndexRegulationPercentage = async () =>
  axios.get<number>(`${APIBASE_REVIVERCONTRACTS}api/contracts/indexRegulationPercentage`);

export const useGetIndexRegulationPercentage = (options?: Partial<UseQueryOptions<number>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<number>({
    queryKey: ["getIndexRegulationPercentage"],
    queryFn: async () => {
      const result = await getIndexRegulationPercentage();
      return result.data;
    },
    ...queryOptions,
  });
};

export const postBulkUpdateContractPrices = async (
  contractIds: number[],
  priceIncrease: number,
  priceIncreaseType: PriceIncreaseType
) => {
  return axios.post(`${APIBASE_REVIVERCONTRACTS}api/contracts/renew/bulk`, {
    contractIds: contractIds,
    priceIncrease: priceIncrease,
    priceIncreaseType: priceIncreaseType,
  });
};
